import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { theme } from "@theme";
import { ErrorBoundaryProps, router } from "expo-router";
import React, { useEffect } from "react";
import { ThemeProvider } from "@shopify/restyle";
import RNRestart from "react-native-restart";
import { useLogout } from "@hooks/useLogout";
import { captureException } from "@utils/captureException";
import { presentIntercomSupport } from "@utils/presentIntercomSupport";

type Props = ErrorBoundaryProps & {
  level: "root" | "app";
};

function ErrorBoundary(props: Props) {
  const logout = useLogout(ErrorBoundary.name);

  useEffect(() => {
    if (props.error) {
      captureException(props.error, {
        channel: "error",
        area: "error-boundary",
        operation: props.level,
      });
    }
  }, [props.error]);

  return (
    <ThemeProvider theme={theme}>
      <LScreen safeArea safeAreaEdges={["top", "bottom"]}>
        <LBox
          flex={3 / 4}
          flexGrow={3 / 4}
          alignItems="center"
          gap="l3"
          justifyContent="center"
        >
          <LText textAlign={"center"} variant={"lead-medium"}>
            Something went wrong
          </LText>

          <LText textAlign={"center"} variant={"h1"}>
            Please try again
          </LText>
        </LBox>
        <LBox
          flex={1 / 4}
          flexGrow={1 / 4}
          gap="m1"
          justifyContent={"flex-end"}
        >
          <LButton
            sentryEventName="error_boundary_contact_support"
            variant="tertiary"
            onPress={async () => {
              await presentIntercomSupport(
                "present-intercom-from-error-boundary",
              );
            }}
            label="Contact Support"
          />
          <LButton
            sentryEventName="error_boundary_back_to_home"
            onPress={async () => {
              switch (props.level) {
                case "root":
                  try {
                    await logout();
                    RNRestart.restart();
                  } catch (e) {
                    console.log(e);
                    RNRestart.restart();
                  }
                  break;
                case "app":
                  router.replace("/");
                  break;
              }
            }}
            label="Back to Home"
          />
        </LBox>
      </LScreen>
    </ThemeProvider>
  );
}

export default ErrorBoundary;
